import { FC, ReactNode } from 'react';
import { Button, cn } from '@crisellevaldez/speculo-ui';
import { SlidersHorizontal } from 'lucide-react';

interface FilterButtonProps {
  onClick: () => void;
  /** Number of currently-applied filters. When > 0 a colored badge renders. */
  activeCount: number;
  /** Display label next to the icon. Defaults to "Filter". */
  label?: string;
  /** speculo-ui Button variant. Defaults to "secondary" to match existing table headers. */
  variant?: 'outline' | 'secondary' | 'primary' | 'ghost';
  /** Extra classes for the button — pass layout/width here. */
  className?: string;
  /** Hide the label below the `sm` breakpoint (icon-only on small screens). */
  hideLabelOnMobile?: boolean;
  /** Override the icon. */
  icon?: ReactNode;
  /**
   * Marks the button as a toggle that is currently on — for consumers where
   * clicking again hides what it opened, like the contacts filter panel.
   *
   * Sets aria-pressed so assistive tech reports a toggle rather than a plain
   * button, and swaps the fill for a bordered grey plus a "Hide" label so the
   * second click is discoverable.
   *
   * A light amber wash with a full-strength amber border. Grey read as
   * disabled rather than held down, and a solid amber fill competed with the
   * table beside it. The alpha composites over the page, so it stays a light
   * tint in either theme while text-foreground keeps the label legible.
   */
  pressed?: boolean;
}

/**
 * The active-count badge, shared so anything showing a filter count reads the
 * same as the Filter button — see the tabs in the contacts FilterDrawer.
 */
export const FILTER_COUNT_BADGE_CLASS =
  'inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full ' +
  'bg-primary text-primary-foreground text-[10px] font-bold leading-none';

/**
 * Reusable "Filter" button with a colored active-count badge. Designed to
 * match across surfaces so every filter affordance reads the same — see
 * EngageTableHeader, ReviveTableHeader, CallStatsHeader (mobile).
 *
 * The button is purely a trigger; consumers own the modal/popover that
 * opens when it's clicked.
 */
export const FilterButton: FC<FilterButtonProps> = ({
  onClick,
  activeCount,
  label = 'Filter',
  variant = 'secondary',
  className,
  hideLabelOnMobile = false,
  icon,
  pressed,
}) => {
  return (
    <Button
      variant={variant}
      onClick={onClick}
      aria-pressed={pressed}
      className={cn(
        'inline-flex items-center gap-1.5 relative h-9',
        pressed && 'border border-primary bg-primary/20 text-foreground hover:bg-primary/20',
        className
      )}
    >
      {icon ?? <SlidersHorizontal className="h-4 w-4" />}
      <span className={hideLabelOnMobile ? 'hidden sm:inline' : undefined}>
        {pressed ? `Hide ${label.toLowerCase()}` : label}
      </span>
      {activeCount > 0 && (
        <span
          className={cn('ml-1', FILTER_COUNT_BADGE_CLASS)}
          aria-label={`${activeCount} active filter${activeCount === 1 ? '' : 's'}`}
        >
          {activeCount}
        </span>
      )}
    </Button>
  );
};
